import React, { useEffect, useState, useCallback } from 'react';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  startOfWeek, endOfWeek, isSameMonth, isToday, getDay,
  addMonths, subMonths
} from 'date-fns';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const DAY_TYPES = ['PRESENT','HALF_DAY','LEAVE','CLIENT_HOLIDAY','WEEKLY_OFF','COMP_OFF'];
const DT_LABEL  = { PRESENT:'Present', HALF_DAY:'Half Day', LEAVE:'Leave', CLIENT_HOLIDAY:'Client Holiday', WEEKLY_OFF:'Weekly Off', COMP_OFF:'Comp Off' };
const DT_COLOR  = { PRESENT:'#10b981', HALF_DAY:'#3b82f6', LEAVE:'#f59e0b', CLIENT_HOLIDAY:'#8b5cf6', WEEKLY_OFF:'#6b7280', COMP_OFF:'#ec4899' };

const needsProject = (t) => ['PRESENT','HALF_DAY'].includes(t);
const needsHours   = (t) => t === 'PRESENT';
const isWeekend    = (day) => { const d = getDay(day); return d === 0 || d === 6; };

const getMonday = (dateStr) => {
  const d   = new Date(dateStr);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
};

export default function Calendar() {
  const { user } = useAuth();
  const [current, setCurrent]       = useState(new Date());
  const [myProjects, setMyProjects] = useState([]);
  const [timesheet, setTimesheet]   = useState(null);
  const [entryMap, setEntryMap]     = useState({});
  const [selected, setSelected]     = useState(null);
  const [panel, setPanel]           = useState(null);
  const [saving, setSaving]         = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode]     = useState(false);

  const monthStart = startOfMonth(current);
  const monthEnd   = endOfMonth(current);
  const calStart   = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd     = endOfWeek(monthEnd,     { weekStartsOn: 1 });
  const allDays    = eachDayOfInterval({ start: calStart, end: calEnd });
  const weeks      = [];
  for (let i = 0; i < allDays.length; i += 7) weeks.push(allDays.slice(i, i + 7));

  const loadMonth = useCallback(async () => {
    const month = current.getMonth() + 1;
    const year  = current.getFullYear();
    const [tsRes, projRes] = await Promise.all([
      api.get(`/timesheets/me?month=${month}&year=${year}`),
      api.get('/projects'),
    ]);
    const allProjects = projRes.data.projects || [];
    const empProjects = allProjects.filter(p =>
      p.status === 'active' && (p.employees || []).some(e => e.id === user.id)
    );
    setMyProjects(empProjects);
    const ts = (tsRes.data.timesheets || [])[0] || null;
    setTimesheet(ts);
    const map = {};
    (ts?.entries || []).forEach(e => { map[e.date] = e; });
    setEntryMap(map);
    if (ts?.status === 'rejected' || ts?.status === 'needs_revision') {
      setEditMode(true);
    } else {
      setEditMode(false);
    }

    // ── Auto-fill all weekends as WEEKLY_OFF ──────────────────────────────
    const fillWeekends = async (tsId, existingMap) => {
      const yr = current.getFullYear();
      const mo = current.getMonth();
      const dim = new Date(yr, mo + 1, 0).getDate();
      const toAdd = [];
      for (let d = 1; d <= dim; d++) {
        const dow = new Date(yr, mo, d).getDay();
        if (dow === 0 || dow === 6) {
          const ds = `${yr}-${String(mo+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          if (!existingMap[ds]) toAdd.push({ date:ds, day_type:'WEEKLY_OFF', project_id:null, hours:null, description:null });
        }
      }
      if (toAdd.length === 0) return;
      const existing = Object.values(existingMap).map(e => ({
        date:e.date, day_type:e.day_type, project_id:e.project_id||null, hours:e.hours||null, description:e.description||null
      }));
      await api.put(`/timesheets/me/${tsId}/entries`, { entries:[...existing,...toAdd] });
      const newMap = { ...existingMap };
      toAdd.forEach(e => { newMap[e.date] = e; });
      setEntryMap(newMap);
    };

    const editableStatuses = ['draft','rejected','needs_revision'];
    if (ts && editableStatuses.includes(ts.status)) {
      fillWeekends(ts.id, map);
    } else if (!ts) {
      // No timesheet yet — create one then fill weekends
      const yr = current.getFullYear();
      const mo = current.getMonth();
      const ws = `${yr}-${String(mo+1).padStart(2,'0')}-01`;
      try {
        const tsRes = await api.post('/timesheets/me', { week_start: ws });
        const newId = tsRes.data.timesheet.id;
        setTimesheet(tsRes.data.timesheet);
        await fillWeekends(newId, {});
      } catch (e) { /* silent */ }
    }
  }, [current, user.id]);

  useEffect(() => { loadMonth(); }, [loadMonth]);

  const isLocked = () => {
    if (!timesheet) return false;
    if (editMode)   return false;
    return ['submitted','mgr_approved','hr_approved','client_approved'].includes(timesheet.status);
  };

  // Auto-save all weekends of the month as WEEKLY_OFF in one batch
  const autoFillWeekends = async (tsId, existingMap, mStart) => {
    const year  = mStart.getFullYear();
    const month = mStart.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Collect all weekend dates that don't already have an entry
    const weekendEntries = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const day = new Date(year, month, d);
      const dow = getDay(day);
      if (dow === 0 || dow === 6) {
        const ds = format(day, 'yyyy-MM-dd');
        if (!existingMap[ds]) {
          weekendEntries.push({ date: ds, day_type: 'WEEKLY_OFF', project_id: null, hours: null, description: null });
        }
      }
    }

    if (weekendEntries.length === 0) return; // all weekends already filled

    // Merge with existing entries
    const existing = Object.values(existingMap).map(e => ({
      date: e.date, day_type: e.day_type,
      project_id: e.project_id || null, hours: e.hours || null, description: e.description || null
    }));

    await api.put(`/timesheets/me/${tsId}/entries`, {
      entries: [...existing, ...weekendEntries]
    });
  };

  const handleDayClick = (day) => {
    if (!isSameMonth(day, monthStart)) return;
    const dateStr = format(day, 'yyyy-MM-dd');
    if (isLocked()) {
      toast('This timesheet is locked — awaiting approval.');
      return;
    }
    const existing = entryMap[dateStr];

    // Open side panel for all days (weekends default to WEEKLY_OFF, weekdays to PRESENT)
    setSelected(dateStr);
    const defaultType = isWeekend(day) ? 'WEEKLY_OFF' : 'PRESENT';
    const defaultProj = myProjects.length === 1 ? myProjects[0].id : '';
    setPanel({
      date:        dateStr,
      day_type:    existing?.day_type    || defaultType,
      project_id:  existing?.project_id  || (needsProject(existing?.day_type || defaultType) ? defaultProj : ''),
      hours:       existing?.hours       || '8',
      description: existing?.description || '',
    });
  };

  const handleSaveEntry = async () => {
    if (!panel) return;
    if (needsProject(panel.day_type) && !panel.project_id) {
      toast.error('Please select a project.'); return;
    }
    if (needsHours(panel.day_type) && (!panel.hours || parseFloat(panel.hours) <= 0)) {
      toast.error('Please enter valid hours.'); return;
    }
    setSaving(true);
    try {
      let tsId = timesheet?.id;
      if (!tsId) {
        const tsRes = await api.post('/timesheets/me', { week_start: format(monthStart, 'yyyy-MM-dd') });
        tsId = tsRes.data.timesheet.id;
      }
      const otherEntries = Object.values(entryMap)
        .filter(e => e.date !== panel.date)
        .map(e => ({ date:e.date, day_type:e.day_type, project_id:e.project_id||null, hours:e.hours||null, description:e.description||null }));
      const newEntry = {
        date:        panel.date,
        day_type:    panel.day_type,
        project_id:  needsProject(panel.day_type) ? panel.project_id : null,
        hours:       panel.day_type === 'HALF_DAY' ? 4 : needsHours(panel.day_type) ? parseFloat(panel.hours) : null,
        description: panel.description || null,
      };
      await api.put(`/timesheets/me/${tsId}/entries`, { entries: [...otherEntries, newEntry] });
      toast.success('Entry saved!');
      setPanel(null); setSelected(null);
      loadMonth();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const handleDeleteEntry = async () => {
    if (!panel || !entryMap[panel.date]) return;
    setSaving(true);
    try {
      const otherEntries = Object.values(entryMap)
        .filter(e => e.date !== panel.date)
        .map(e => ({ date:e.date, day_type:e.day_type, project_id:e.project_id||null, hours:e.hours||null, description:e.description||null }));
      await api.put(`/timesheets/me/${timesheet.id}/entries`, { entries: otherEntries });
      toast.success('Entry removed.');
      setPanel(null); setSelected(null);
      loadMonth();
    } catch { toast.error('Failed to remove.'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    if (!timesheet) { toast.error('No entries to submit.'); return; }
    if (Object.keys(entryMap).length === 0) { toast.error('Please log at least one day before submitting.'); return; }
    if (!window.confirm("Submit this month's timesheet for manager approval?")) return;
    setSubmitting(true);
    try {
      await api.post(`/timesheets/me/${timesheet.id}/submit`);
      toast.success('Submitted for manager approval!');
      setEditMode(false); loadMonth();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit.');
    } finally { setSubmitting(false); }
  };

  const handleResubmit = async () => {
    if (!window.confirm('Resubmit this timesheet for manager approval?')) return;
    setSubmitting(true);
    try {
      await api.post(`/timesheets/me/${timesheet.id}/submit`);
      toast.success('Resubmitted!');
      setEditMode(false); loadMonth();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed.');
    } finally { setSubmitting(false); }
  };

  const STATUS_BADGE = { draft:'badge-gray', submitted:'badge-amber', mgr_approved:'badge-blue', hr_approved:'badge-teal', client_approved:'badge-green', rejected:'badge-red', needs_revision:'badge-amber' };
  const totalBillable = Object.values(entryMap).reduce((s,e) => s + (e.day_type==='PRESENT' ? parseFloat(e.hours||0) : e.day_type==='HALF_DAY' ? 4 : 0), 0);
  const countType = (type) => Object.values(entryMap).filter(e=>e.day_type===type).length;

  return (
    <div style={{ display:'flex', gap:0 }}>
      <div style={{ flex:1, paddingRight: panel ? 360 : 0, transition:'padding-right .2s' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
          <div>
            <h1 style={{ fontSize:22, fontWeight:700, letterSpacing:'-.4px' }}>My Timesheet</h1>
            <p style={{ fontSize:13, color:'#6b7280', marginTop:3 }}>Click any day to log attendance — weekends are highlighted in purple</p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button className="cal-nav" onClick={() => setCurrent(subMonths(current,1))}>‹</button>
            <span style={{ fontWeight:700, fontSize:16, minWidth:140, textAlign:'center' }}>{format(current,'MMMM yyyy')}</span>
            <button className="cal-nav" onClick={() => setCurrent(addMonths(current,1))}>›</button>
          </div>
        </div>

        {/* Status bar */}
        {timesheet && (
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, padding:'10px 16px', background:'#1a1d27', borderRadius:10, border:'1px solid #2a2d3a', flexWrap:'wrap' }}>
            <span style={{ fontSize:13, fontWeight:600 }}>{format(current,'MMMM yyyy')} Timesheet</span>
            <span className={`badge ${STATUS_BADGE[timesheet.status]}`}>{timesheet.status.replace(/_/g,' ')}</span>
            {timesheet.status === 'rejected' && (
              <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:'auto', flexWrap:'wrap' }}>
                <span style={{ fontSize:12.5, color:'#ef4444' }}>
                  ⚠ Rejected{timesheet.mgr_comments ? `: "${timesheet.mgr_comments}"` : ''}
                </span>
                {!editMode
                  ? <button className="btn btn-danger btn-sm" onClick={() => setEditMode(true)}>✏ Edit &amp; Resubmit</button>
                  : <button className="btn btn-primary btn-sm" disabled={submitting} onClick={handleResubmit}>📤 Resubmit</button>
                }
              </div>
            )}
          </div>
        )}

        {/* Edit mode banner */}
        {editMode && timesheet?.status === 'rejected' && (
          <div style={{ padding:'10px 16px', background:'rgba(245,158,11,.08)', border:'1px solid rgba(245,158,11,.25)', borderRadius:10, marginBottom:14, fontSize:13, color:'#f59e0b', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span>✏ Edit mode — update your entries then resubmit</span>
            <button className="btn btn-primary btn-sm" disabled={submitting} onClick={handleResubmit}>📤 Resubmit</button>
          </div>
        )}

        {/* Summary chips */}
        {Object.keys(entryMap).length > 0 && (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
            {countType('PRESENT')>0        && <span style={{fontSize:12,color:'#10b981',background:'rgba(16,185,129,.1)',padding:'3px 10px',borderRadius:20}}>✓ {countType('PRESENT')} Present</span>}
            {countType('HALF_DAY')>0       && <span style={{fontSize:12,color:'#3b82f6',background:'rgba(59,130,246,.1)',padding:'3px 10px',borderRadius:20}}>½ {countType('HALF_DAY')} Half Day</span>}
            {countType('LEAVE')>0          && <span style={{fontSize:12,color:'#f59e0b',background:'rgba(245,158,11,.1)',padding:'3px 10px',borderRadius:20}}>🏖 {countType('LEAVE')} Leave</span>}
            {countType('CLIENT_HOLIDAY')>0 && <span style={{fontSize:12,color:'#8b5cf6',background:'rgba(139,92,246,.1)',padding:'3px 10px',borderRadius:20}}>🎉 {countType('CLIENT_HOLIDAY')} Holiday</span>}
            {countType('WEEKLY_OFF')>0     && <span style={{fontSize:12,color:'#6b7280',background:'rgba(107,114,128,.1)',padding:'3px 10px',borderRadius:20}}>🔴 {countType('WEEKLY_OFF')} Off</span>}
            {countType('COMP_OFF')>0       && <span style={{fontSize:12,color:'#ec4899',background:'rgba(236,72,153,.1)',padding:'3px 10px',borderRadius:20}}>💆 {countType('COMP_OFF')} Comp Off</span>}
            <span style={{fontSize:12,color:'#6c63ff',background:'rgba(108,99,255,.1)',padding:'3px 10px',borderRadius:20,fontWeight:600}}>⏱ {totalBillable.toFixed(1)} billable hrs</span>
          </div>
        )}

        {/* Day-of-week headers — Sat/Sun highlighted */}
        <div className="cal-grid" style={{ marginBottom:4 }}>
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
            <div
              key={d}
              className="cal-dow"
              style={{
                color:      ['Sat','Sun'].includes(d) ? '#818cf8' : undefined,
                background: ['Sat','Sun'].includes(d) ? 'rgba(99,102,241,0.08)' : undefined,
                borderRadius: 6,
              }}
            >
              {d}
            </div>
          ))}
        </div>

        {/* Calendar weeks */}
        {weeks.map((week, wi) => (
          <div key={wi} className="cal-grid" style={{ marginBottom:3 }}>
            {week.map(day => {
              const ds      = format(day,'yyyy-MM-dd');
              const entry   = entryMap[ds];
              const same    = isSameMonth(day, monthStart);
              const weekend = same && isWeekend(day);
              const locked  = same && isLocked();
              return (
                <div
                  key={ds}
                  className={[
                    'cal-day',
                    !same   ? 'other-month' : '',
                    isToday(day) ? 'today' : '',
                    selected === ds ? 'selected' : '',
                    locked  ? 'locked'  : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleDayClick(day)}
                  style={{
                    // Weekend columns: subtle purple tint background
                    background: weekend
                      ? selected === ds
                        ? 'rgba(99,102,241,0.18)'
                        : 'rgba(99,102,241,0.05)'
                      : undefined,
                    borderColor: weekend && !entry ? 'rgba(99,102,241,0.25)' : undefined,
                    cursor: locked ? 'not-allowed' : 'pointer',
                  }}
                >
                  {/* Date number — purple for weekends */}
                  <div
                    className="cal-date"
                    style={{ color: weekend ? '#818cf8' : undefined }}
                  >
                    {format(day,'d')}
                  </div>



                  {/* Saved entry */}
                  {entry && same && (
                    <>
                      <div className={`day-chip dt-${entry.day_type}`}>{DT_LABEL[entry.day_type]}</div>
                      {(entry.hours || entry.day_type==='HALF_DAY') && (
                        <div className="day-hours">{entry.day_type==='HALF_DAY'?'4':entry.hours}h</div>
                      )}
                      {entry.project && (
                        <div style={{fontSize:10,color:'#6b7280',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{entry.project.name}</div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {/* Submit button */}
        <div style={{ marginTop:20, display:'flex', justifyContent:'flex-end', gap:10 }}>
          {(!timesheet || timesheet.status === 'draft') && Object.keys(entryMap).length > 0 && (
            <button className="btn btn-primary" disabled={submitting} onClick={handleSubmit}>
              📤 Submit {format(current,'MMMM')} Timesheet for Approval
            </button>
          )}
        </div>

        {/* Legend */}
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginTop:16, alignItems:'center' }}>
          {DAY_TYPES.map(dt => (
            <div key={dt} style={{ display:'flex', alignItems:'center', gap:5 }}>
              <div style={{ width:10, height:10, borderRadius:3, background:DT_COLOR[dt] }}/>
              <span style={{ fontSize:11.5, color:'#6b7280' }}>{DT_LABEL[dt]}</span>
            </div>
          ))}
          <span style={{ fontSize:11.5, color:'#818cf8', marginLeft:8, borderLeft:'1px solid #2a2d3a', paddingLeft:8 }}>
            ■ Weekend (click to log)
          </span>
        </div>
      </div>

      {/* Side panel */}
      {panel && (
        <div className="side-panel">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div className="side-panel-title">{format(new Date(panel.date + 'T00:00:00'),'EEEE, MMM d')}</div>
              {isWeekend(new Date(panel.date + 'T00:00:00')) && (
                <div style={{ fontSize:11, color:'#818cf8', marginTop:2 }}>Weekend — all day types available</div>
              )}
            </div>
            <button onClick={() => { setPanel(null); setSelected(null); }} style={{background:'none',border:'none',color:'#6b7280',cursor:'pointer',fontSize:18,lineHeight:1}}>✕</button>
          </div>

          <div className="divider"/>

          {/* Day Type buttons */}
          <div>
            <div style={{ fontSize:12, fontWeight:500, color:'#9ca3af', marginBottom:8 }}>Day Type</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
              {DAY_TYPES.map(dt => (
                <button
                  key={dt}
                  onClick={() => setPanel({
                    ...panel,
                    day_type:   dt,
                    project_id: needsProject(dt) ? (panel.project_id || (myProjects.length===1 ? myProjects[0].id : '')) : '',
                    hours:      dt==='PRESENT' ? (panel.hours||'8') : '',
                  })}
                  style={{
                    padding:'9px 8px', borderRadius:8,
                    border:`1px solid ${panel.day_type===dt ? DT_COLOR[dt] : '#2a2d3a'}`,
                    background: panel.day_type===dt ? `${DT_COLOR[dt]}22` : '#0f1117',
                    color: panel.day_type===dt ? DT_COLOR[dt] : '#9ca3af',
                    fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all .15s'
                  }}
                >{DT_LABEL[dt]}</button>
              ))}
            </div>
          </div>

          {/* Project */}
          {needsProject(panel.day_type) && (
            <div className="field">
              <label>Project {myProjects.length===1 ? '(auto-selected)' : '*'}</label>
              {myProjects.length === 0 ? (
                <div style={{fontSize:12.5,color:'#ef4444',padding:'8px 12px',background:'rgba(239,68,68,.08)',borderRadius:8,border:'1px solid rgba(239,68,68,.2)'}}>
                  ⚠ No projects assigned. Contact HR.
                </div>
              ) : myProjects.length === 1 ? (
                <div style={{padding:'9px 12px',background:'rgba(108,99,255,.08)',borderRadius:8,border:'1px solid rgba(108,99,255,.2)',fontSize:13,color:'#6c63ff',fontWeight:500}}>
                  📁 {myProjects[0].name}
                </div>
              ) : (
                <select value={panel.project_id} onChange={e=>setPanel({...panel,project_id:e.target.value})}>
                  <option value="">Select project…</option>
                  {myProjects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
            </div>
          )}

          {/* Hours */}
          {needsHours(panel.day_type) && (
            <div className="field">
              <label>Hours *</label>
              <input type="number" min="0.5" max="24" step="0.5" value={panel.hours} onChange={e=>setPanel({...panel,hours:e.target.value})} placeholder="8"/>
            </div>
          )}

          {panel.day_type === 'HALF_DAY' && (
            <div style={{padding:'10px 12px',background:'rgba(59,130,246,.08)',borderRadius:8,border:'1px solid rgba(59,130,246,.2)',fontSize:12.5,color:'#3b82f6'}}>
              ℹ Half Day = 4 hours (fixed)
            </div>
          )}

          {/* Description */}
          <div className="field">
            <label>{needsProject(panel.day_type) ? 'Work Description' : 'Reason (optional)'}</label>
            <textarea rows={3} value={panel.description} onChange={e=>setPanel({...panel,description:e.target.value})} placeholder={needsProject(panel.day_type) ? 'What did you work on?' : 'e.g. Comp off, festival holiday…'}/>
          </div>

          <div className="divider"/>

          <button
            className="btn btn-primary btn-full"
            disabled={saving || (needsProject(panel.day_type) && !panel.project_id && myProjects.length>1) || (needsHours(panel.day_type) && !panel.hours)}
            onClick={handleSaveEntry}
          >
            {saving ? 'Saving…' : '✓ Save Entry'}
          </button>

          {entryMap[panel.date] && !saving && (
            <button className="btn btn-danger btn-full" onClick={handleDeleteEntry}>
              🗑 Remove Entry
            </button>
          )}
        </div>
      )}
    </div>
  );
}